/**
 * Seller listings API client — wires the private-seller dashboard AND the
 * live publishing flow (VehiclePublish) to the Epic 6 endpoints:
 * create draft → verify VIN → upload the 6 photos → disclosure → price →
 * activate, plus GET /vehicles/mine + the lifecycle actions. All
 * authenticated (HMAC + JWT, SELLER role).
 */
import { apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';

export type SellerListingStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'SUSPENDED';

/** Enum values the backend DTOs accept (kernel Transmission/FuelType). */
export type ApiTransmission = 'MANUAL' | 'AUTOMATIC' | 'CVT' | 'DUAL_CLUTCH';
export type ApiFuelType = 'GASOLINE' | 'DIESEL' | 'HYBRID' | 'ELECTRIC' | 'LPG';

/** The six photo categories a listing must carry before it can be activated. */
export const REQUIRED_PHOTO_CATEGORIES = [
  'EXTERIOR_FRONT',
  'EXTERIOR_REAR',
  'INTERIOR_DASHBOARD',
  'INTERIOR_SEATS',
  'ENGINE',
  'TIRES',
] as const;
export type PhotoCategory = (typeof REQUIRED_PHOTO_CATEGORIES)[number];

export interface SellerListing {
  id: string;
  make: string;
  model: string;
  year: number;
  mileage: number;
  status: SellerListingStatus;
  listingPrice?: number;
  suggestedPrice?: number | null;
  vinVerified: boolean;
}

interface ApiVehicle {
  vehicleId: string;
  make: string;
  model: string;
  year: number;
  mileage: number;
  status: SellerListingStatus;
  listingPrice?: number;
  suggestedPrice?: number | null;
  vinVerifiedAt?: string;
}

const toListing = (v: ApiVehicle): SellerListing => ({
  id: v.vehicleId,
  make: v.make,
  model: v.model,
  year: v.year,
  mileage: v.mileage,
  status: v.status,
  listingPrice: v.listingPrice,
  suggestedPrice: v.suggestedPrice ?? null,
  vinVerified: !!v.vinVerifiedAt,
});

export async function fetchMyListings(): Promise<SellerListing[]> {
  const res = await apiGet<{ vehicles: ApiVehicle[] }>('/api/v1/vehicles/mine');
  return (res.vehicles ?? []).map(toListing);
}

export async function fetchListing(id: string): Promise<SellerListing> {
  const res = await apiGet<{ vehicle: ApiVehicle }>(`/api/v1/vehicles/${id}`);
  return toListing(res.vehicle);
}

export const activateListing = (id: string) => apiPost(`/api/v1/vehicles/${id}/activate`, {});
export const pauseListing = (id: string) => apiPost(`/api/v1/vehicles/${id}/pause`, {});
export const archiveListing = (id: string) => apiPost(`/api/v1/vehicles/${id}/archive`, {});

/* ── Publishing flow (BC-01-E01/E02/E04) ─────────────────────────────── */

export interface CreateDraftInput {
  make: string;
  model: string;
  year: number;
  mileage: number;
  transmission: ApiTransmission;
  fuel: ApiFuelType;
  color: string;
  vin: string;
  region?: string;
  listingPrice?: number;
}

export interface DisclosureInput {
  accidentHistory: boolean;
  accidentDetails?: string;
  previousOwners: number;
  serviceRecordsAvailable: boolean;
  outstandingLiens: boolean;
  lienDetails?: string;
  knownDefects: string;
  lastServiceDate?: string;
}

interface PresignResult {
  seq: number;
  uploadUrl: string;
  s3Key: string;
  expiresAt: string;
}

/** Create a DRAFT listing; returns the new listing id. */
export async function createDraft(input: CreateDraftInput): Promise<string> {
  const res = await apiPost<{ vehicle: ApiVehicle }>('/api/v1/vehicles/draft', input);
  return res.vehicle.vehicleId;
}

/** Update draft specs / price (re-runs the pricing suggestion server-side). */
export async function updateListing(
  id: string,
  patch: Partial<CreateDraftInput>,
): Promise<SellerListing> {
  const res = await apiPut<{ vehicle: ApiVehicle }>(`/api/v1/vehicles/${id}`, patch);
  return toListing(res.vehicle);
}

/** Run the VIN through the verification ACL. Returns whether it verified. */
export async function verifyVin(id: string): Promise<boolean> {
  const res = await apiPost<{ vehicle: ApiVehicle }>(`/api/v1/vehicles/${id}/verify-vin`, {});
  return !!res.vehicle.vinVerifiedAt;
}

/**
 * Upload one photo for a category. Two-step presign → PUT → confirm.
 * Against the dev stub storage the presigned host is unreachable
 * (`*.s3.local`, marked `X-Amz-Stub=true`), so we skip the PUT and go
 * straight to confirm — the confirm step keys the row CONFIRMED regardless.
 * When real S3 lands (Week-1 B2) the same code path PUTs the bytes.
 */
export async function uploadPhoto(id: string, category: PhotoCategory, file: File): Promise<void> {
  const presigned = await apiPost<PresignResult>(`/api/v1/vehicles/${id}/photos/presign`, {
    category,
    contentType: file.type,
    fileName: file.name,
  });

  const isStub = presigned.uploadUrl.includes('X-Amz-Stub=true');
  if (!isStub) {
    const put = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!put.ok) throw new Error(`No se pudo subir la foto (${put.status}).`);
  }

  await apiPost(`/api/v1/vehicles/${id}/photos/confirm`, {
    seq: presigned.seq,
    s3Key: presigned.s3Key,
  });
}

/** Submit the seller disclosure form. */
export const submitDisclosure = (id: string, disclosure: DisclosureInput) =>
  apiPut(`/api/v1/vehicles/${id}/disclosure`, disclosure);
